import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { handleExportPDF } from "../../../utils/exportToPDF";
import ViewLogisticQuotesExcelExport from "../ViewLogisticQuotesTableExport";

const parseArrayField = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const getExchangeRateForItem = (item, currencyArray) => {
  if (
    item?.exchangeRate !== undefined &&
    item?.exchangeRate !== null &&
    item?.exchangeRate !== ""
  ) {
    return item.exchangeRate;
  }

  const matchedCurrency = currencyArray.find(
    (currency) => currency?.name === item?.currency,
  );
  return matchedCurrency?.rate ?? "-";
};

const getVendorDisplayList = (draft) => {
  const directVendors = parseArrayField(draft?.vendors).map((vendor) => {
    if (typeof vendor === "string") return vendor;
    return vendor?.firmName || vendor?.name || vendor?.email || "";
  });

  const vendorRfqs = parseArrayField(draft?.vendorRFQS).map((vendorRfq) => {
    const vendor = vendorRfq?.vendor;
    if (!vendor) return "";

    if (vendor.firmName && vendor.name) {
      return `${vendor.name} (${vendor.firmName})`;
    }

    return vendor.firmName || vendor.name || vendor.email || "";
  });

  return [...new Set([...directVendors, ...vendorRfqs].filter(Boolean))];
};

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const [date, time] = dateString.split("T");
  if (!date) return "";
  const [year, month, day] = date.split("-");
  const [hours, minutes] = time?.split(":") || [];
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const withUnit = (value, unit) => {
  if (value === null || value === undefined || value === "") return "-";
  return `${value} ${unit}`;
};

const ViewLogisticDraftDetails = () => {
  const { logisticDraftId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const [draft, setDraft] = useState(null);
  const [buyerName, setBuyerName] = useState("-");
  const [isLoading, setIsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const pdfRef = useRef();
  const awardRef = useRef();
  const [awarded, setAwarded] = useState();
  const [comment, setComment] = useState();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    const getDraft = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/logistic-draft/get-by-id?logisticDraftId=${logisticDraftId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        const draftData = res.data;
        setDraft(draftData);

        const draftBuyerName =
          draftData?.orgUser?.name ||
          draftData?.buyerName ||
          draftData?.createdByName;

        if (draftBuyerName) {
          setBuyerName(draftBuyerName);
        } else if (draftData?.username) {
          try {
            const buyerRes = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${draftData.username}`,
              {
                headers: { Authorization: `Bearer ${storedUser.accessToken}` },
              },
            );
            setBuyerName(buyerRes?.data?.name || draftData.username);
          } catch {
            setBuyerName(draftData.username);
          }
        } else {
          setBuyerName("-");
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };
    getDraft();

    const getAwarded = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/logistics-quote/awarded?logisticDraftId=${logisticDraftId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setAwarded(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    getAwarded();

    const getComments = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/comment/logistic?logisticDraftId=${logisticDraftId}&username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setComment(res.data || []);
      } catch (error) {
        setComment([]);
      }
    };
    getComments();

    const getQuotes = async () => {
        try {
          setLoading(true);
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/logistic/${logisticDraftId}/quotes`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          setQuotes(res.data || []);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      };
      getQuotes();
  }, [logisticDraftId, storedUser.accessToken]);

  const comparisonQuotes = useMemo(() => {
      const vendorGroups = new Map();
  
      (quotes || []).forEach((quote) => {
        const vendorKey =
          quote.vendorEmail ||
          quote.vendorFirmName ||
          quote.vendorName ||
          quote.logisticQuoteId;
        if (!vendorGroups.has(vendorKey)) {
          vendorGroups.set(vendorKey, []);
        }
        vendorGroups.get(vendorKey).push(quote);
      });
  
      return Array.from(vendorGroups.values()).flatMap((vendorQuotes) => {
        const sortedQuotes = vendorQuotes
          .slice()
          .sort(
            (a, b) =>
              new Date(a.createdDate || 0).getTime() -
              new Date(b.createdDate || 0).getTime(),
          );
  
        const vendorName =
          sortedQuotes[sortedQuotes.length - 1]?.vendorFirmName ||
          sortedQuotes[sortedQuotes.length - 1]?.vendorName ||
          "Vendor";
  
        if (sortedQuotes.length === 1) {
          return [
            {
              ...sortedQuotes[0],
              comparisonLabel: vendorName,
              isCurrentVersion: true,
            },
          ];
        }
  
        const previousQuote = sortedQuotes[0];
        const currentQuote = sortedQuotes[sortedQuotes.length - 1];
  
        return [
          {
            ...previousQuote,
            comparisonLabel: `${vendorName} (Initial Quote)`,
            isCurrentVersion: false,
          },
          {
            ...currentQuote,
            comparisonLabel: `${vendorName} (Final Quote)`,
            isCurrentVersion: true,
          },
        ];
      });
    }, [quotes]);

  const handleBack = () => {
    if (location.pathname.startsWith("/logistic-cxo-dashboard/")) {
      navigate("/logistic-cxo-dashboard");
      return;
    }
    if (location.pathname.startsWith("/logistic-purchase-head-dashboard/")) {
      navigate("/logistic-purchase-head-dashboard");
      return;
    }
    navigate("/logistic-dashboard");
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!draft) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Logistic details not found.</Typography>
      </Box>
    );
  }

  const currencyArray = parseArrayField(draft.currencies);
  const workItems = parseArrayField(draft.workItems).sort(
    (a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0),
  );
  const vendorList = getVendorDisplayList(draft);
  const grandTotal = workItems.reduce(
    (sum, item) => sum + parseFloat(item?.total || 0),
    0,
  );

  const detailRows = [
    { label: "Logistic ID", value: draft.logisticDraftId },
    ...(draft.creationDate
      ? [{ label: "Creation Date", value: formatDateTime(draft.creationDate) }]
      : []),
    ...(draft.submissionDate
      ? [
          {
            label: "Submission Date",
            value: formatDateTime(draft.submissionDate),
          },
        ]
      : []),
    { label: "Mode of Consignment", value: draft.modeOfConsignment || "-" },
    { label: "Consignee", value: draft.consignee || "-" },
    { label: "Invoice No", value: draft.invoiceNo || "-" },
    { label: "Transit Time", value: withUnit(draft.transitTime, "Days") },
    { label: "Origin", value: draft.origin || "-" },
    { label: "Destination", value: draft.destination || "-" },
    { label: "No of Pallets", value: draft.noOfPallets || "-" },
    { label: "Address", value: draft.address || "-" },
    { label: "Gross Weight", value: withUnit(draft.grossWeight, "Kg") },
    { label: "Length", value: withUnit(draft.length, "inches") },
    { label: "Breadth", value: withUnit(draft.breadth, "inches") },
    { label: "Height", value: withUnit(draft.height, "inches") },
    { label: "Volume (CBM)", value: withUnit(draft.volumeCBM, "CBM") },
  ];

  return (
    <div>
      {exporting && (
        <div className="uploading-div">
          Exporting PDF... This may take a while.{" "}
          <CircularProgress color="#fff" />
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "end", gap: "20px" }}>
        {awarded && awarded.length > 0 && (
          <Button
            className="btn-contained"
            sx={{ width: "200px" }}
            onClick={async () => {
              try {
                setExporting(true);
                await handleExportPDF(awardRef, "Logistics_RFQ_Awarded");
              } catch (e) {
                console.log(e);
              } finally {
                setExporting(false);
              }
            }}
          >
            Export Awarded PDF
          </Button>
        )}
        <Button
          className="btn-contained"
          sx={{ width: "200px" }}
          onClick={async () => {
            try {
              setExporting(true);
              await handleExportPDF(pdfRef, "Logistics_RFQ_Details");
            } catch (e) {
              console.log(e);
            } finally {
              setExporting(false);
            }
          }}
        >
          Export RFQ PDF
        </Button>
      </div>
      <div ref={pdfRef}>
        <Box className="dashboard-container" px={10}>
          <Typography className="form-title">Logistic RFQ Details</Typography>
          <Grid2 container>
            {detailRows.map((row, index) => (
              <React.Fragment key={row.label}>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderTop: index === 0 ? "1px solid #000" : undefined }}
                >
                  {row.label}
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{
                    borderTop: index === 0 ? "1px solid #000" : undefined,
                    borderRight: "1px solid #000",
                  }}
                >
                  {row.value}
                </Grid2>
              </React.Fragment>
            ))}
          </Grid2>
        </Box>

        <h2>Work Items Details</h2>
        <Box className="dashboard-container" px={10}>
          <table
            border={1}
            style={{
              borderCollapse: "collapse",
              width: "100%",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={{ padding: "20px" }}>#</th>
                <th>Particulars</th>
                <th>Unit</th>
                <th>Quantity</th>
                <th>Currency</th>
                <th>Exchange Rate</th>
                <th>Rate</th>
                <th>Total (in &#8377;)</th>
              </tr>
            </thead>
            <tbody>
              {workItems
                .sort((a, b) => a.itemOrder - b.itemOrder)
                .map((item, index) => (
                  <tr key={index} style={{ textAlign: "center" }}>
                    <td style={{ padding: "20px" }}>
                      {item.itemOrder ?? index + 1}
                    </td>
                    <td>{item.particulars || "-"}</td>
                    <td>{item.unit || "-"}</td>
                    <td>{item.quantity || "-"}</td>
                    <td>{item.currency || "-"}</td>
                    <td>{getExchangeRateForItem(item, currencyArray)}</td>
                    <td>{item.newRate ?? item.rate ?? "-"}</td>
                    <td>
                      {new Intl.NumberFormat("en-IN", {
                        style: "decimal",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(parseFloat(item?.total || 0))}
                    </td>
                  </tr>
                ))}
              <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
                <td
                  colSpan={7}
                  style={{ textAlign: "right", paddingRight: "10px" }}
                >
                  Grand Total (in &#8377;):
                </td>
                <td
                  style={{
                    width: "150px",
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  {new Intl.NumberFormat("en-IN", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Box className="dashboard-container" px={10}>
          <Typography variant="h6">Approvers</Typography>
          <Grid2 container>
            {draft?.logisticApprovers && (
              <React.Fragment>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderTop: "1px solid #000" }}
                >
                  Buyer
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{
                    borderTop: "1px solid #000",
                    borderRight: "1px solid #000",
                  }}
                >
                  {buyerName}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  PURCHASE MANAGER
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {draft.logisticApprovers[0]?.purchaseHead?.name ||
                    "Loading..."}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  FINAL APPROVER 1
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {draft.logisticApprovers[0]?.awardee1
                    ? draft.logisticApprovers[0]?.awardee1?.name
                    : "N/A" || "Loading..."}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  FINAL APPROVER 2
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {draft.logisticApprovers[0]?.awardee2?.name || "Loading..."}
                </Grid2>
              </React.Fragment>
            )}
          </Grid2>
        </Box>

        <Box className="dashboard-container" px={10}>
          <Typography variant="h6">Vendors</Typography>

          <Box>
            {(draft?.vendors || []).map((email, index) => {
              const matchedVendor = draft?.vendorList?.find(
                (v) => v.email === email,
              );

              return (
                <p key={index}>
                  {email} - {matchedVendor ? matchedVendor.vrStatus : "PENDING"}
                </p>
              );
            })}
          </Box>
        </Box>

        {currencyArray.length > 0 && (
          <Box className="dashboard-container" px={10}>
            <Typography variant="h6">Currency Details</Typography>
            <table
              border={1}
              style={{
                borderCollapse: "collapse",
                width: "100%",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "20px" }}>Currency</th>
                  <th>Exchange Rate</th>
                </tr>
              </thead>
              <tbody>
                {currencyArray.map((currency, index) => (
                  <tr key={index} style={{ textAlign: "center" }}>
                    <td style={{ padding: "20px" }}>{currency.name}</td>
                    <td>{currency.rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
        {awarded && awarded.length > 0 && (
          <Box className="dashboard-container" px={10} mt={4}>
            <Typography variant="h6">Awarded Quotes</Typography>

            <table
              border={1}
              cellPadding="20px"
              style={{ width: "100%", borderCollapse: "collapse" }}
            >
              <thead>
                <tr style={{ backgroundColor: "#e0f7fa", fontWeight: "bold" }}>
                  <th>Sr. No.</th>
                  <th>Particulars</th>
                  <th>Unit</th>
                  <th>Quantity</th>
                  <th>Currency</th>
                  <th>Ex. Rate</th>
                  <th>Rate</th>
                  <th>Total (₹)</th>
                </tr>
              </thead>

              <tbody>
                {awarded.flatMap((quote) =>
                  (quote.quoteItems || [])
                    .sort((a, b) => a.srNo - b.srNo)
                    .map((item) => (
                      <tr
                        key={item.logisticQuoteItemId}
                        style={{ textAlign: "center" }}
                      >
                        <td>{item.srNo}</td>
                        <td>{item.particulars}</td>
                        <td>{item.unit}</td>
                        <td>{item.quantity}</td>
                        <td>{item.currency}</td>
                        <td>{getExchangeRateForItem(item, currencyArray)}</td>
                        <td>{item.rate}</td>
                        <td>
                          {new Intl.NumberFormat("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(item.total)}
                        </td>
                      </tr>
                    )),
                )}
                <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
                  <td
                    colSpan={7}
                    style={{ textAlign: "right", paddingRight: "10px" }}
                  >
                    Grand Total (in &#8377;):
                  </td>
                  <td
                    style={{
                      width: "150px",
                      textAlign: "center",
                      padding: "20px",
                    }}
                  >
                    {new Intl.NumberFormat("en-IN", {
                      style: "decimal",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(
                      awarded[0]?.quoteItems?.reduce(
                        (sum, item) => sum + parseFloat(item?.total || 0),
                        0,
                      ),
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>
        )}

        {comment && draft?.status === "AWARDED" && (
          <Box className="dashboard-container" px={10}>
            <Typography variant="h6">Comments</Typography>
            <table
              border="1"
              cellPadding="20px"
              cellSpacing="0"
              style={{ width: "100%", borderCollapse: "collapse" }}
            >
              <thead>
                <tr style={{ backgroundColor: "#e0f7fa", fontWeight: "bold" }}>
                  <th>Name</th>
                  <th>Designation</th>
                  <th>Date</th>
                  <th>Comment</th>
                </tr>
              </thead>
              <tbody>
                {comment.map((c, index) => (
                  <tr key={index}>
                    <td>{c.orgUser?.name || "—"}</td>
                    <td>{c.orgUser?.userRoleName.replace("_", " ") || "—"}</td>
                    <td>
                      {new Date(c.createdDate).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </td>
                    <td>{c.comment || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </div>

      {awarded && awarded.length > 0 && (
        <div
          ref={awardRef}
          style={{
            position: "absolute",
            top: "-9999px",
            left: "-9999px",
            height: "auto",
            width: "auto",
            paddingInline: "10px",
          }}
        >
          <Box px={10}>
            {/* ✅ Header */}
            <Box py={2}>
              <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
                Awarded Logistics Quotes
              </Typography>

              <Typography sx={{ fontSize: "13px" }}>
                <b>Logistic ID:</b> {draft?.logisticDraftId}
              </Typography>

              <Typography sx={{ fontSize: "13px" }}>
                <b>Created By:</b> {buyerName}
              </Typography>

              <Typography sx={{ fontSize: "13px" }}>
                <b>Creation Date:</b> {formatDateTime(draft?.createdDate)}
              </Typography>
            </Box>

            {comment && draft?.status === "AWARDED" && (
              <Box className="dashboard-container">
                <Typography variant="h6">Comments</Typography>
                <table
                  border="1"
                  cellPadding="6px"
                  cellSpacing="0"
                  style={{ width: "100%", borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr
                      style={{ backgroundColor: "#e0f7fa", fontWeight: "bold" }}
                    >
                      <th>Name</th>
                      <th>Designation</th>
                      <th>Date</th>
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comment.map((c, index) => (
                      <tr key={index}>
                        <td>{c.orgUser?.name || "—"}</td>
                        <td>
                          {c.orgUser?.userRoleName.replace("_", " ") || "—"}
                        </td>
                        <td>
                          {new Date(c.createdDate).toLocaleString("en-IN", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </td>
                        <td>{c.comment || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
            <ViewLogisticQuotesExcelExport
              quotes={comparisonQuotes}
              logisticDraft={draft}
            />
            {/* ✅ Calculate Grand Total */}
            {(() => {
              const allItems =
                awarded?.flatMap((q) => q.quoteItems || []) || [];

              const grandTotal = allItems.reduce(
                (sum, item) => sum + Number(item?.total || 0),
                0,
              );

              return (
                <>
                  {/* ✅ Awarded Table */}
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: "bold", mt: 2 }}
                  >
                    Awarded Items
                  </Typography>

                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "8px",
                      fontSize: "12px",
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={thStyle}>Sr No</th>
                        <th style={thStyle}>Particulars</th>
                        <th style={thStyle}>Unit</th>
                        <th style={thStyle}>Qty</th>
                        <th style={thStyle}>Currency</th>
                        <th style={thStyle}>Rate</th>
                        <th style={thStyle}>Total (₹)</th>
                      </tr>
                    </thead>

                    <tbody>
                      {allItems
                        .sort((a, b) => a.srNo - b.srNo)
                        .map((item) => (
                          <tr key={item.logisticQuoteItemId}>
                            <td style={tdStyle}>{item.srNo}</td>
                            <td style={tdStyle}>{item.particulars}</td>
                            <td style={tdStyle}>{item.unit}</td>
                            <td style={tdStyle}>{item.quantity}</td>
                            <td style={tdStyle}>{item.currency}</td>
                            <td style={tdStyle}>{item.rate}</td>
                            <td style={tdStyle}>
                              {new Intl.NumberFormat("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(item.total)}
                            </td>
                          </tr>
                        ))}

                      {/* ✅ Grand Total Row */}
                      <tr>
                        <td
                          colSpan={6}
                          style={{
                            ...tdStyle,
                            fontWeight: "bold",
                            textAlign: "right",
                          }}
                        >
                          Grand Total:
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            fontWeight: "bold",
                          }}
                        >
                          {new Intl.NumberFormat("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(grandTotal)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </>
              );
            })()}
          </Box>
        </div>
      )}

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
      </Box>
    </div>
  );
};

export default ViewLogisticDraftDetails;

const thStyle = {
  border: "1px solid #ccc",
  padding: "6px",
  backgroundColor: "#e0f7fa",
  fontWeight: "bold",
};

const tdStyle = {
  border: "1px solid #ccc",
  padding: "6px",
  textAlign: "center",
};
